import { useEffect, useState } from 'react';
import { Alert, Descriptions, Input, Modal, Typography } from 'antd';
import { ExclamationCircleFilled } from '@ant-design/icons';

export type DangerTargetField = {
  label: string;
  value: string;
  copyable?: boolean;
};

export type DangerConfirmModalProps = {
  open: boolean;
  /** 弹窗标题 */
  title: string;
  /** 强提示主文案：一句话说清这一步的后果 */
  warning: string;
  /** 后果明细，逐条列出 */
  details?: string[];
  /** 受影响对象的关键信息，供用户核对 */
  target?: DangerTargetField[];
  /** 需要用户原样输入的确认文本（不传则点确认按钮即执行） */
  confirmText?: string;
  /** 确认按钮文案 */
  okText: string;
  /** 提示条级别，默认 error */
  alertType?: 'error' | 'warning';
  /** 确认按钮是否使用危险样式，默认 true */
  okDanger?: boolean;
  loading?: boolean;
  onOk: () => void;
  onCancel: () => void;
};

/**
 * 危险操作二次确认弹窗：强提示 + 影响对象核对 + 可选的手动输入确认。
 * 用于删除、密钥轮换等不可逆或影响面大的操作，避免误触。
 */
export function DangerConfirmModal({
  open,
  title,
  warning,
  details,
  target,
  confirmText,
  okText,
  alertType = 'error',
  okDanger = true,
  loading,
  onOk,
  onCancel,
}: DangerConfirmModalProps) {
  const [typed, setTyped] = useState('');

  useEffect(() => {
    if (open) setTyped('');
  }, [open]);

  const matched = !confirmText || typed.trim() === confirmText;

  return (
    <Modal
      open={open}
      title={
        <span>
          <ExclamationCircleFilled
            style={{ color: alertType === 'error' ? '#dc2626' : '#d97706', marginRight: 8 }}
          />
          {title}
        </span>
      }
      okText={okText}
      cancelText="取消"
      okButtonProps={{ danger: okDanger, disabled: !matched, loading }}
      cancelButtonProps={{ disabled: loading }}
      onOk={onOk}
      onCancel={onCancel}
      maskClosable={false}
      width={520}
      destroyOnClose
    >
      <Alert
        type={alertType}
        showIcon
        message={warning}
        description={
          details?.length ? (
            <ul style={{ margin: '4px 0 0', paddingInlineStart: 18 }}>
              {details.map((d) => (
                <li key={d}>{d}</li>
              ))}
            </ul>
          ) : undefined
        }
        style={{ marginBottom: 16 }}
      />

      {!!target?.length && (
        <>
          <Typography.Text type="secondary">本次操作影响的对象</Typography.Text>
          <Descriptions
            size="small"
            column={1}
            bordered
            style={{ marginTop: 8 }}
            items={target.map((t) => ({
              key: t.label,
              label: t.label,
              children: (
                <Typography.Text copyable={t.copyable} style={{ wordBreak: 'break-all' }}>
                  {t.value}
                </Typography.Text>
              ),
            }))}
          />
        </>
      )}

      {confirmText && (
        <div style={{ marginTop: 16 }}>
          <Typography.Text>
            请输入 <Typography.Text code>{confirmText}</Typography.Text> 以确认操作
          </Typography.Text>
          <Input
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder={`原样输入：${confirmText}`}
            autoComplete="off"
            status={typed && !matched ? 'error' : undefined}
            style={{ marginTop: 8 }}
          />
        </div>
      )}
    </Modal>
  );
}

export default DangerConfirmModal;
